import React from 'react';
import { Divider } from '@mui/material';
import { useIsMobile } from '../../../hooks/useIsMobile';
import {
  AI_COLORS, AI_FONTS,
  TipBox, CheckItem, VideoDrive,
  PageHero, ContentWrap, SectionTitle, SubTitle,
} from '../shared';

export default function HabitosSaudaveisAdulto() {
  const isMobile = useIsMobile();

  const alimentacao = [
    {
      icon: '🥗',
      titulo: 'Consuma mais alimentos naturais',
      texto:
        'Alimentos frescos (frutas, verduras e legumes). Recomenda-se o consumo diário de três porções de frutas e três porções de legumes e verduras nas refeições.',
    },
    {
      icon: '💧',
      titulo: 'Beba bastante água',
      texto:
        'A água auxilia na produção de saliva, ajuda na limpeza da boca e reduz o mau hálito.',
    },
    {
      icon: '🍬',
      titulo: 'Evite excesso de açúcar',
      texto:
        'Doces, refrigerantes, bolos e alimentos ultraprocessados aumentam o risco de cáries quando consumidos em excesso.',
    },
    {
      icon: '🍽️',
      titulo: 'Faça refeições regulares',
      texto:
        'Evite "beliscar" o dia todo para reduzir a exposição dos dentes aos ácidos produzidos pelas bactérias.',
    },
    {
      icon: '🥛',
      titulo: 'Consuma alimentos ricos em cálcio',
      texto:
        'Leite, queijo e iogurte natural são fontes de cálcio, nutriente importante para a manutenção da saúde dos ossos e dentes ao longo da vida.',
    },
    {
      icon: '🚫',
      titulo: 'Evite ultraprocessados',
      texto:
        'Alimentos ultraprocessados possuem excesso de açúcar, sódio e aditivos prejudiciais à saúde.',
    },
    {
      icon: '✅',
      titulo: 'Tenha acompanhamento profissional',
      texto:
        'Consultas regulares com dentista e nutricionista ajudam na prevenção de doenças e promoção da saúde.',
    },
  ];

  const beneficiosAtividade = [
    { icon: '❤️', texto: 'Melhora da saúde do coração' },
    { icon: '🦴', texto: 'Fortalecimento dos ossos e músculos' },
    { icon: '⚖️', texto: 'Melhora do equilíbrio e prevenção de quedas' },
    { icon: '😊', texto: 'Redução do estresse e melhora do humor' },
    { icon: '🧠', texto: 'Estímulo da memória e da capacidade cognitiva' },
    { icon: '🩺', texto: 'Auxílio no controle da pressão arterial, diabetes e colesterol' },
  ];

  const atividades = [
    { icon: '🚶', label: 'Caminhadas' },
    { icon: '🧘', label: 'Alongamentos' },
    { icon: '💪', label: 'Treino de força' },
    { icon: '🚲', label: 'Atividades orientadas' },
  ];

  return (
    <div style={{ fontFamily: AI_FONTS.body, background: 'transparent' }}>
      <PageHero
        title="Hábitos Saudáveis"
        subtitle="Adotar hábitos saudáveis é a base da prevenção de doenças e do envelhecimento com qualidade de vida. Pequenas mudanças no dia a dia fazem grande diferença."
        audio="habitos-saudaveis.m4a"
      />

      <ContentWrap>
        <VideoDrive id="1nKWd5fpDi9LjVey7_oBncftB25Ki_YIH" title="Saúde Geral" />

        {/* Alimentação */}
        <SectionTitle style={{ marginTop: 0 }}>Alimentação e Saúde Bucal</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          Uma alimentação equilibrada vai muito além da estética e da saúde do corpo: ela também é
          fundamental para manter dentes e gengivas saudáveis.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 14,
            marginBottom: 20,
          }}
        >
          {alimentacao.map((h, i) => (
            <div
              key={i}
              style={{
                background: i % 2 === 0 ? AI_COLORS.bgBlue50 : AI_COLORS.bgPurple50,
                borderRadius: 12,
                padding: '18px 20px',
              }}
            >
              <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>{h.icon}</div>
              <strong
                style={{
                  fontFamily: AI_FONTS.title,
                  color: AI_COLORS.blue900,
                  display: 'block',
                  marginBottom: 6,
                  fontSize: '1rem',
                }}
              >
                {h.titulo}
              </strong>
              <p style={{ margin: 0, lineHeight: 1.6, fontSize: '0.93rem' }}>{h.texto}</p>
            </div>
          ))}
        </div>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Atividade física */}
        <SectionTitle>Atividade Física na Vida Adulta e no Envelhecimento</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          A prática regular de atividade física contribui para um envelhecimento saudável, ajudando
          na força muscular, equilíbrio, mobilidade, autonomia e qualidade de vida.
        </p>

        <SubTitle>Principais benefícios:</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 12,
            marginBottom: 20,
          }}
        >
          {beneficiosAtividade.map((b, i) => (
            <div
              key={i}
              style={{
                background: i % 2 === 0 ? AI_COLORS.bgBlue50 : AI_COLORS.bgPurple50,
                borderRadius: 10,
                padding: '12px 16px',
                display: 'flex',
                gap: 10,
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '1.3rem', flexShrink: 0 }}>{b.icon}</span>
              <span style={{ fontSize: '0.93rem' }}>{b.texto}</span>
            </div>
          ))}
        </div>

        <SubTitle>Tipos de atividade recomendados:</SubTitle>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
            gap: 12,
            marginBottom: 20,
          }}
        >
          {atividades.map((a, i) => (
            <div
              key={i}
              style={{
                background: AI_COLORS.bgBlue50,
                borderRadius: 12,
                padding: '16px',
                textAlign: 'center',
                border: `2px solid ${AI_COLORS.bgBlue100}`,
              }}
            >
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>{a.icon}</div>
              <strong
                style={{
                  fontFamily: AI_FONTS.title,
                  color: AI_COLORS.blue900,
                  fontSize: '0.95rem',
                }}
              >
                {a.label}
              </strong>
            </div>
          ))}
        </div>

        <TipBox>
          Antes de iniciar qualquer atividade física, consulte um médico para avaliação e
          orientação personalizada.
        </TipBox>

        <Divider style={{ margin: '32px 0', borderColor: AI_COLORS.bgBlue100 }} />

        {/* Não fumar */}
        <SectionTitle>Não Fumar e Evitar Bebidas Alcoólicas</SectionTitle>
        <p style={{ lineHeight: 1.75 }}>
          Fumar e consumir bebidas alcoólicas em excesso podem causar diversos problemas de saúde.
          Eles também aumentam o risco de doenças da boca.
        </p>

        <SubTitle>Benefícios de reduzir ou parar:</SubTitle>
        <ul style={{ padding: 0, margin: '0 0 16px' }}>
          <CheckItem>Mais disposição e energia no dia a dia</CheckItem>
          <CheckItem>Melhor respiração e função pulmonar</CheckItem>
          <CheckItem>Redução do risco de câncer bucal e doença periodontal</CheckItem>
          <CheckItem>Melhora geral da saúde e qualidade de vida</CheckItem>
        </ul>

        <div
          style={{
            background: AI_COLORS.bgBlue50,
            borderLeft: `4px solid ${AI_COLORS.blue400}`,
            borderRadius: 10,
            padding: '18px 22px',
          }}
        >
          <p
            style={{
              fontFamily: AI_FONTS.title,
              color: AI_COLORS.blue900,
              fontSize: '1.05rem',
              margin: '0 0 8px',
            }}
          >
            Higiene Bucal Diária
          </p>
          <p style={{ margin: 0, lineHeight: 1.75 }}>
            Escovar os dentes pelo menos 3 vezes ao dia, usar fio dental diariamente e visitar o
            dentista a cada 6 meses são hábitos inegociáveis para manter a saúde bucal na fase
            adulta e na terceira idade.
          </p>
        </div>
      </ContentWrap>
    </div>
  );
}
